import React, { useState, useEffect, useMemo } from 'react';
import ResultFigure from './shared/ResultFigure';
import ExploreControls, { useExploration } from './shared/ExploreControls';
import { Scenario } from '../types/model';
import {
  getArrayShape,
  downloadBlob,
  alphabeticalPermutation,
  permuteArray1D,
  permuteRows2D,
  permuteCols2D,
  safeRatio,
} from '../utils/dataProcessing';
import {
  createMultiScenarioBarChartConfig,
  createHeatmapConfig,
  createMultiScenarioHeatmapConfig,
  SCENARIO_COLORS
} from '../utils/chartConfigs';
import { Download } from 'lucide-react';
import OutputVariableSelect from './shared/OutputVariableSelect';
import { modelView } from '../utils/variableMetadata';
import { apiService } from '../services/api';
import { showToast } from '../utils/toast';

interface ScenarioVisualizationProps {
  scenarios: Scenario[];
  framework?: string;
  dataset?: string;
  initialVariable?: string;
}

const ScenarioVisualization: React.FC<ScenarioVisualizationProps> = ({ scenarios: sourceScenarios, framework, dataset, initialVariable = '' }) => {
  const scenarios = useMemo(() => sourceScenarios.map(s => ({...s, result: s.result ? modelView(s.result) : null})), [sourceScenarios]);
  const [selectedVariable, setSelectedVariable] = useState<string>(initialVariable);
  const [selectedScenarioIds, setSelectedScenarioIds] = useState<string[]>([]);
  const [isDownloading, setIsDownloading] = useState(false);

  // Get variables from first scenario
  const firstScenario = scenarios[0];
  const variables = useMemo(() => {
    if (!firstScenario?.result) return [];
    return Object.keys(firstScenario.result.sol);
  }, [firstScenario]);

  // Initialize selected variable when variables change.
  useEffect(() => {
    if (variables.length > 0 && !variables.includes(selectedVariable)) {
      setSelectedVariable(variables.includes('hats.C') ? 'hats.C' : variables[0]);
    }
  }, [variables, selectedVariable]);

  // Initialize selected scenarios when scenarios change
  useEffect(() => {
    const scenarioIds = scenarios.map(s => s.id);
    
    setSelectedScenarioIds(prevSelected => {
      // Add any new scenarios to selection
      const newIds = scenarioIds.filter(id => !prevSelected.includes(id));
      // Keep only valid ids that still exist
      const validPrevIds = prevSelected.filter(id => scenarioIds.includes(id));
      
      // If nothing changed, return previous to avoid re-render
      if (newIds.length === 0 && validPrevIds.length === prevSelected.length) {
        return prevSelected;
      }
      
      return [...validPrevIds, ...newIds];
    });
  }, [scenarios]);

  const selectedObject = selectedVariable.split('.')[1];

  const exploration = useExploration(selectedObject, getArrayShape(firstScenario?.result?.sol[selectedVariable]));
  const { fixed: fixedDims, values: dimValues } = exploration;

  // Pre-compute alphabetical sort permutation for countries.
  // Must be called unconditionally (Rules of Hooks) — safe to compute even when
  // firstScenario.result is null; we guard with the fallback empty array.
  const countryPerm = useMemo(
    () => alphabeticalPermutation(firstScenario?.result?.country_list ?? []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [firstScenario?.result?.country_list]
  );
  const sortedCountries = useMemo(
    () => countryPerm.map(i => (firstScenario?.result?.country_list ?? [])[i]),
    [countryPerm, firstScenario?.result?.country_list]
  );

  const displayNames = useMemo(
    () => firstScenario?.result?.variable_display_names ?? {}, [firstScenario],
  );

  if (scenarios.length === 0) {
    return (
      <div style={styles.empty}>
        <p>Select a scenario to view results.</p>
      </div>
    );
  }

  if (!firstScenario?.result) return null;
  if (!selectedVariable || !variables.includes(selectedVariable)) return null;


  const selectedScenarios = scenarios.filter(s => selectedScenarioIds.includes(s.id));

  // Helper functions


  const axes = firstScenario.result.input_metadata[selectedVariable].axes;
  const variableLabel = firstScenario.result.input_metadata[selectedVariable].meaning;
  const getLabels = (dimSize: number, axis: number): string[] => {
    if (axes[axis].kind === 'country') {
      return sortedCountries;
    } else if (axes[axis].kind === 'sector') {
      return firstScenario.result!.sector_list;
    }
    return Array.from({ length: dimSize }, (_, i) => `Item ${i}`);
  };

  // Returns the sort permutation for country dimensions, null for others
  const getDimPerm = (_dimSize: number, axis: number): number[] | null => {
    if (axes[axis].kind === 'country') return countryPerm;
    return null;
  };

  // Translate sorted-index to original data index
  const origIdx = (dimSize: number, sortedIdx: number, axis: number): number => {
    const p = getDimPerm(dimSize, axis);
    return p ? p[sortedIdx] : sortedIdx;
  };

  const data = firstScenario.result.sol[selectedVariable];
  const shape = getArrayShape(data);
  const ndim = shape.length;

  const isRatio = selectedVariable.startsWith('hats.');

  /**
   * Extract display data for a 1D variable from a scenario result.
   * Returns ratio (cf/baseline - 1) when isRatio is true, else raw CF values.
   */
  const getScenario1DData = (scenario: typeof selectedScenarios[0]): number[] => {
    const raw = scenario.result!.sol[selectedVariable] as number[];
    if (isRatio && scenario.result!.baseline_sol) {
      const base = scenario.result!.baseline_sol[selectedVariable] as number[];
      if (!base) return raw;
      return raw.map((v, i) => safeRatio(v, base[i]));
    }
    return raw;
  };

  /**
   * Extract display data for a 2D variable from a scenario result.
   */
  const getScenario2DData = (scenario: typeof selectedScenarios[0]): number[][] => {
    const raw = scenario.result!.sol[selectedVariable] as number[][];
    if (isRatio && scenario.result!.baseline_sol) {
      const base = scenario.result!.baseline_sol[selectedVariable] as number[][];
      if (!base) return raw;
      return raw.map((row, i) => row.map((v, j) => safeRatio(v, base[i]?.[j] ?? 0)));
    }
    return raw;
  };

  /**
   * Extract display data for a 3D variable from a scenario result.
   */
  const getScenario3DData = (scenario: typeof selectedScenarios[0]): number[][][] => {
    const raw = scenario.result!.sol[selectedVariable] as number[][][];
    if (isRatio && scenario.result!.baseline_sol) {
      const base = scenario.result!.baseline_sol[selectedVariable] as number[][][];
      if (!base) return raw;
      return raw.map((layer, i) =>
        layer.map((row, j) =>
          row.map((v, k) => safeRatio(v, base[i]?.[j]?.[k] ?? 0))
        )
      );
    }
    return raw;
  };

  const renderComparison = () => {
    if (selectedScenarios.length === 0) {
      return (
        <div style={styles.infoBox}>
          Select a scenario to view results.
        </div>
      );
    }

    if (ndim === 0) {
      // Scalar values
      return (
        <div style={styles.scalarContainer}>
          {selectedScenarios.map(scenario => {
            const raw = scenario.result!.sol[selectedVariable] as number;
            const baseRaw = isRatio && scenario.result!.baseline_sol
              ? (scenario.result!.baseline_sol[selectedVariable] as number)
              : null;
            const displayValue = baseRaw !== null
              ? (raw / (baseRaw + 1e-30) - 1) * 100
              : raw;

            return (
              <div key={scenario.id} style={styles.scalarCard}>
                <div style={styles.scalarHeader}>
                  <strong>{scenario.name}</strong>
                </div>
                <div style={styles.scalarValue}>
                  {isRatio
                    ? `${displayValue >= 0 ? '+' : ''}${displayValue.toFixed(2)}%`
                    : raw.toFixed(4)}
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    if (ndim === 1) {
      // 1D - show ratio or raw values
      const perm = getDimPerm(shape[0], 0);
      const labels = getLabels(shape[0], 0);

      const scenarioData = selectedScenarios.map(scenario => {
        const display = getScenario1DData(scenario);
        return {
          name: scenario.name,
          data: perm ? permuteArray1D(display, perm) : display,
        };
      });

      if (fixedDims.includes(0)) {
        const index = dimValues[0] || 0;
        return <div className="single-value-results">{scenarioData.map((series, i) => {
          const value = isRatio ? series.data[index] * 100 : series.data[index];
          return <div key={i}><h3>{series.name}</h3><strong>{Number.isFinite(value) ? value.toLocaleString(undefined, { maximumSignificantDigits: 8 }) : '—'}{isRatio && '%'}</strong><p>{labels[index]} · {variableLabel}</p></div>;
        })}</div>;
      }

      return (
        <div className="chart-container">
          <ResultFigure
            option={createMultiScenarioBarChartConfig(
              labels,
              scenarioData,
              selectedScenarios.length === 1 ? selectedScenarios[0].name : 'Multi-Scenario Comparison',
              variableLabel,
              isRatio
            )}
            style={{ height: 'clamp(360px, 52vh, 520px)' }}
          />
        </div>
      );
    }

    if (ndim === 2) {
      return render2DVariable();
    } else if (ndim === 3) {
      return render3DVariable();
    }

    return null;
  };

  const render2DVariable = () => {
    const dimInfo = axes.map((a, i) => ({name: a.name, size: shape[i]}));
    const perm0 = getDimPerm(shape[0], 0);
    const perm1 = getDimPerm(shape[1], 1);
    const labels0 = getLabels(shape[0], 0);
    const labels1 = getLabels(shape[1], 1);

    const freeDims = [0, 1].filter(d => !fixedDims.includes(d));
    const numFreeDims = freeDims.length;


    if (numFreeDims === 2) {
      // Show 2D heatmap - permute both axes for alphabetical display
      if (selectedScenarios.length === 1) {
        const scenario = selectedScenarios[0];
        const rawValues = getScenario2DData(scenario);
        let displayValues = rawValues;
        if (perm0) displayValues = permuteRows2D(displayValues, perm0);
        if (perm1) displayValues = permuteCols2D(displayValues, perm1);

        return (
          <div>
            <div style={styles.dimensionInfo}>
              <div style={styles.dimensionExplanation}>
                Choose a view and filters above.
              </div>
            </div>

            <div className="chart-container">
              <ResultFigure
                option={createHeatmapConfig(
                  labels1,
                  labels0,
                  displayValues,
                  `${scenario.name} - ${variableLabel}`,
                  dimInfo[1]?.name || 'Columns',
                  dimInfo[0]?.name || 'Rows',
                  isRatio
                )}
                className="scenario-chart" style={{ height: 'clamp(420px, 65vh, 680px)' }}
              />
            </div>
          </div>
        );
      } else {
        // Multiple scenarios - use split-cell comparison heatmap
        const scenarioData = selectedScenarios.map((scenario, idx) => {
          let d = getScenario2DData(scenario);
          if (perm0) d = permuteRows2D(d, perm0);
          if (perm1) d = permuteCols2D(d, perm1);
          return {
            name: scenario.name,
            data: d,
            color: SCENARIO_COLORS[idx % SCENARIO_COLORS.length],
          };
        });

        return (
          <div>
            <div style={styles.dimensionInfo}>
              <div style={styles.dimensionExplanation}>
                Each cell is divided into {selectedScenarios.length} segments, one per scenario
              </div>
              <div style={styles.scenarioLegend}>
                {selectedScenarios.map((s, idx) => (
                  <span key={s.id} style={styles.legendItem}>
                    <span
                      style={{
                        ...styles.legendColor,
                        backgroundColor: SCENARIO_COLORS[idx % SCENARIO_COLORS.length]
                      }}
                    />
                    {s.name}
                  </span>
                ))}
              </div>
            </div>

            <div className="chart-container">
              <ResultFigure
                option={createMultiScenarioHeatmapConfig(
                  labels1,
                  labels0,
                  scenarioData,
                  `${variableLabel} - Multi-Scenario Comparison`,
                  dimInfo[1]?.name || 'Columns',
                  dimInfo[0]?.name || 'Rows',
                  isRatio
                )}
                style={{ height: '900px' }}
              />
            </div>
          </div>
        );
      }
    } else if (numFreeDims === 1) {
      // Show 1D bar chart with multiple scenarios
      const freeDim = freeDims[0];
      const fixedDim = fixedDims[0];
      const freeLabels = freeDim === 0 ? labels0 : labels1;
      const sortedFixedIdx = dimValues[fixedDim] || 0;
      const fixedLabel = (fixedDim === 0 ? labels0 : labels1)[sortedFixedIdx];
      const origFixedIdx = origIdx(fixedDim === 0 ? shape[0] : shape[1], sortedFixedIdx, fixedDim);
      const freePerm = freeDim === 0 ? perm0 : perm1;

      const scenarioData = selectedScenarios.map(scenario => {
        const values = getScenario2DData(scenario);
        let rawSlice: number[];
        if (fixedDim === 0) {
          rawSlice = values[origFixedIdx];
        } else {
          rawSlice = values.map(row => row[origFixedIdx]);
        }
        return {
          name: scenario.name,
          data: freePerm ? permuteArray1D(rawSlice, freePerm) : rawSlice,
        };
      });

      return (
        <div>
          <div style={styles.dimensionInfo}>
            <div style={styles.dimensionExplanation}>
              Viewing: <strong>{dimInfo[freeDim]?.name}</strong> • Fixed: <strong>{dimInfo[fixedDim]?.name} = {fixedLabel}</strong>
            </div>
          </div>

          <div className="chart-container">
            <ResultFigure
              option={createMultiScenarioBarChartConfig(
                freeLabels,
                scenarioData,
                `${variableLabel} [${dimInfo[fixedDim]?.name}=${fixedLabel}]`,
                variableLabel,
                isRatio
              )}
              className="scenario-chart" style={{ height: 'clamp(420px, 65vh, 680px)' }}
            />
          </div>
        </div>
      );
    } else {
      // numFreeDims === 0: Show scalar values for all scenarios
      const orig0 = origIdx(shape[0], dimValues[0] || 0, 0);
      const orig1 = origIdx(shape[1], dimValues[1] || 0, 1);
      const scalarValues = selectedScenarios.map(scenario => {
        const values = getScenario2DData(scenario);
        return {
          scenario,
          value: values[orig0][orig1]
        };
      });

      const label0 = labels0[dimValues[0] || 0];
      const label1 = labels1[dimValues[1] || 0];

      return (
        <div>
          <div style={styles.dimensionInfo}>
            <div style={styles.dimensionExplanation}>
              {dimInfo[0]?.name} = <strong>{label0}</strong> • {dimInfo[1]?.name} = <strong>{label1}</strong>
            </div>
          </div>

          <div style={styles.scalarContainer}>
            {scalarValues.map(({ scenario, value }) => (
              <div key={scenario.id} style={styles.scalarCard}>
                <div style={styles.scalarHeader}>
                  <strong>{scenario.name}</strong>
                </div>
                <div style={styles.scalarValue}>
                  {isRatio
                    ? `${value >= 0 ? '+' : ''}${(value * 100).toFixed(2)}%`
                    : value.toFixed(6)}
                </div>
                <div style={{fontSize: '14px', color: 'var(--color-text-subtle)'}}>
                  {variableLabel}[{label0}, {label1}]
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }
  };

  const render3DVariable = () => {
    const dimInfo = axes.map((a, i) => ({name: a.name, size: shape[i]}));
    const perm0 = getDimPerm(shape[0], 0);
    const perm1 = getDimPerm(shape[1], 1);
    const perm2 = getDimPerm(shape[2], 2);
    const perms3 = [perm0, perm1, perm2];
    const labels0 = getLabels(shape[0], 0);
    const labels1 = getLabels(shape[1], 1);
    const labels2 = getLabels(shape[2], 2);

    // Translate sorted index to original data index for 3D slicing
    const origIdx3 = (dim: number, sortedIdx: number): number => {
      const p = perms3[dim];
      return p ? p[sortedIdx] : sortedIdx;
    };

    const freeDims = [0, 1, 2].filter(d => !fixedDims.includes(d));
    const numFreeDims = freeDims.length;


    if (numFreeDims === 3) {
      // No dimensions fixed
      return (
        <div>
          <div style={styles.dimensionInfo}>

          </div>
          <div style={styles.infoBox}>
            Choose a dimension above to view a slice.
          </div>
        </div>
      );
    } else if (numFreeDims === 2) {
      // Show 2D heatmap
      const fixedDim = fixedDims[0];
      const sortedFixedIdx = dimValues[fixedDim] || 0;
      const origFixedIdx = origIdx3(fixedDim, sortedFixedIdx);
      const fixedLabel = (fixedDim === 0 ? labels0 : fixedDim === 1 ? labels1 : labels2)[sortedFixedIdx];

      // Determine axis labels and permutations for the free dimensions
      let xLabels: string[];
      let yLabels: string[];
      let xAxisName: string;
      let yAxisName: string;
      let yPerm: number[] | null;
      let xPerm: number[] | null;

      if (fixedDim === 0) {
        yLabels = labels1; xLabels = labels2;
        yAxisName = dimInfo[1]?.name || 'Dim 1'; xAxisName = dimInfo[2]?.name || 'Dim 2';
        yPerm = perm1; xPerm = perm2;
      } else if (fixedDim === 1) {
        yLabels = labels0; xLabels = labels2;
        yAxisName = dimInfo[0]?.name || 'Dim 0'; xAxisName = dimInfo[2]?.name || 'Dim 2';
        yPerm = perm0; xPerm = perm2;
      } else {
        yLabels = labels0; xLabels = labels1;
        yAxisName = dimInfo[0]?.name || 'Dim 0'; xAxisName = dimInfo[1]?.name || 'Dim 1';
        yPerm = perm0; xPerm = perm1;
      }

      if (selectedScenarios.length === 1) {
        const scenario = selectedScenarios[0];
        const values3D = getScenario3DData(scenario);

        let rawSlice: number[][];
        if (fixedDim === 0) {
          rawSlice = values3D[origFixedIdx];
        } else if (fixedDim === 1) {
          rawSlice = values3D.map(row => row[origFixedIdx]);
        } else {
          rawSlice = values3D.map(row => row.map(col => col[origFixedIdx]));
        }
        let sliceData = rawSlice;
        if (yPerm) sliceData = permuteRows2D(sliceData, yPerm);
        if (xPerm) sliceData = permuteCols2D(sliceData, xPerm);

        return (
          <div>
            <div style={styles.dimensionInfo}>
              <div style={styles.dimensionExplanation}>
                Fixed: <strong>{dimInfo[fixedDim]?.name} = {fixedLabel}</strong> • Rows: <strong>{yAxisName}</strong> • Columns: <strong>{xAxisName}</strong>
              </div>
            </div>

            <div className="chart-container">
              <ResultFigure
                option={createHeatmapConfig(
                  xLabels,
                  yLabels,
                  sliceData,
                  `${scenario.name} - ${variableLabel} [${dimInfo[fixedDim]?.name}=${fixedLabel}]`,
                  xAxisName,
                  yAxisName,
                  isRatio
                )}
                className="scenario-chart" style={{ height: 'clamp(420px, 65vh, 680px)' }}
              />
            </div>
          </div>
        );
      } else {
        // Multiple scenarios - split-cell heatmap
        const scenarioData = selectedScenarios.map((scenario, idx) => {
          const values3D = getScenario3DData(scenario);
          let rawSlice: number[][];
          if (fixedDim === 0) {
            rawSlice = values3D[origFixedIdx];
          } else if (fixedDim === 1) {
            rawSlice = values3D.map(row => row[origFixedIdx]);
          } else {
            rawSlice = values3D.map(row => row.map(col => col[origFixedIdx]));
          }
          let d = rawSlice;
          if (yPerm) d = permuteRows2D(d, yPerm);
          if (xPerm) d = permuteCols2D(d, xPerm);
          return {
            name: scenario.name,
            data: d,
            color: SCENARIO_COLORS[idx % SCENARIO_COLORS.length],
          };
        });

        return (
          <div>
            <div style={styles.dimensionInfo}>
              <div style={styles.dimensionExplanation}>
                Fixed: <strong>{dimInfo[fixedDim]?.name} = {fixedLabel}</strong> • Each cell divided into {selectedScenarios.length} segments
              </div>
              <div style={styles.scenarioLegend}>
                {selectedScenarios.map((s, idx) => (
                  <span key={s.id} style={styles.legendItem}>
                    <span
                      style={{
                        ...styles.legendColor,
                        backgroundColor: SCENARIO_COLORS[idx % SCENARIO_COLORS.length]
                      }}
                    />
                    {s.name}
                  </span>
                ))}
              </div>
            </div>

            <div className="chart-container">
              <ResultFigure
                option={createMultiScenarioHeatmapConfig(
                  xLabels,
                  yLabels,
                  scenarioData,
                  `${variableLabel} [${dimInfo[fixedDim]?.name}=${fixedLabel}]`,
                  xAxisName,
                  yAxisName,
                  isRatio
                )}
                style={{ height: '900px' }}
              />
            </div>
          </div>
        );
      }
    } else if (numFreeDims === 1) {
      // Show 1D bar chart
      const freeDim = freeDims[0];
      const freeLabels = freeDim === 0 ? labels0 : freeDim === 1 ? labels1 : labels2;

      const fixedLabelStrs = fixedDims.map(d => {
        const lbls = d === 0 ? labels0 : d === 1 ? labels1 : labels2;
        return lbls[dimValues[d] || 0];
      });
      const origFixed3 = fixedDims.map(d => origIdx3(d, dimValues[d] || 0));

      const scenarioData = selectedScenarios.map(scenario => {
        const values3D = getScenario3DData(scenario);
        let rawSlice: number[];
        if (fixedDims.includes(0) && fixedDims.includes(1)) {
          rawSlice = values3D[origFixed3[0]][origFixed3[1]];
        } else if (fixedDims.includes(0) && fixedDims.includes(2)) {
          rawSlice = values3D[origFixed3[0]].map(row => row[origFixed3[1]]);
        } else {
          rawSlice = values3D.map(layer => layer[origFixed3[0]][origFixed3[1]]);
        }
        const fp = perms3[freeDim];
        return {
          name: scenario.name,
          data: fp ? permuteArray1D(rawSlice, fp) : rawSlice,
        };
      });

      const fixedDesc = fixedDims.map((d, i) => `${dimInfo[d]?.name}=${fixedLabelStrs[i]}`).join(', ');

      return (
        <div>
          <div style={styles.dimensionInfo}>
            <div style={styles.dimensionExplanation}>
              Viewing: <strong>{dimInfo[freeDim]?.name}</strong> • Fixed: <strong>{fixedDesc}</strong>
            </div>
          </div>

          <div className="chart-container">
            <ResultFigure
              option={createMultiScenarioBarChartConfig(
                freeLabels,
                scenarioData,
                `${variableLabel} [${fixedDesc}]`,
                variableLabel,
                isRatio
              )}
              className="scenario-chart" style={{ height: 'clamp(420px, 65vh, 680px)' }}
            />
          </div>
        </div>
      );
    } else {
      // numFreeDims === 0: Show scalar values for all scenarios
      const orig0 = origIdx3(0, dimValues[0] || 0);
      const orig1 = origIdx3(1, dimValues[1] || 0);
      const orig2 = origIdx3(2, dimValues[2] || 0);
      const scalarValues = selectedScenarios.map(scenario => {
        const values3D = getScenario3DData(scenario);
        return {
          scenario,
          value: values3D[orig0][orig1][orig2]
        };
      });

      const label0 = labels0[dimValues[0] || 0];
      const label1 = labels1[dimValues[1] || 0];
      const label2 = labels2[dimValues[2] || 0];

      const fixedDesc = `${dimInfo[0]?.name}=${label0}, ${dimInfo[1]?.name}=${label1}, ${dimInfo[2]?.name}=${label2}`;

      return (
        <div>
          <div style={styles.dimensionInfo}>
            <div style={styles.dimensionExplanation}>
              Fixed: <strong>{fixedDesc}</strong>
            </div>
          </div>

          <div style={styles.scalarContainer}>
            {scalarValues.map(({ scenario, value }) => (
              <div key={scenario.id} style={styles.scalarCard}>
                <div style={styles.scalarHeader}>
                  <strong>{scenario.name}</strong>
                </div>
                <div style={styles.scalarValue}>
                  {isRatio
                    ? `${value >= 0 ? '+' : ''}${(value * 100).toFixed(2)}%`
                    : value.toFixed(6)}
                </div>
                <div style={{fontSize: '14px', color: 'var(--color-text-subtle)'}}>
                  {variableLabel}[{label0}, {label1}, {label2}]
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }
  };

  const selectedScenariosForView = scenarios.filter(s => selectedScenarioIds.includes(s.id));

  const handleDownload = async () => {
    if (!framework || !dataset || selectedScenariosForView.length === 0) return;

    // Excel export supports composable shocks — include every solved
    // selected scenario regardless of tariff/kappa mix.
    const solvedForExport = sourceScenarios.filter(s => selectedScenarioIds.includes(s.id) && s.status === 'solved');
    if (solvedForExport.length === 0) {
      showToast('No solved scenarios selected to export.', 'error');
      return;
    }

    setIsDownloading(true);
    try {
      const blob = await apiService.downloadScenariosExcel(framework, dataset, solvedForExport);
      const filename = `Scenarios_${framework}_${dataset}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      downloadBlob(blob, filename);
    } catch (err: any) {
      // When responseType is 'blob', Axios wraps error responses as Blobs too.
      // Attempt to read the blob as JSON to extract the detail message.
      let msg = err?.message || 'Unknown error';
      const responseData = err?.response?.data;
      if (responseData instanceof Blob) {
        try {
          const text = await responseData.text();
          const parsed = JSON.parse(text);
          if (parsed?.detail) msg = parsed.detail;
        } catch {
          // Ignore parse errors — use the original message
        }
      } else if (responseData?.detail) {
        msg = responseData.detail;
      }
      showToast(`Download failed: ${msg}`, 'error');
    } finally {
      setIsDownloading(false);
    }
  };

  return <div className="scenario-results">
    <div className="results-toolbar">
      <OutputVariableSelect value={selectedVariable} onChange={setSelectedVariable}
        paths={variables} metadata={firstScenario.result.input_metadata} names={displayNames} />
      {framework && dataset && <button onClick={handleDownload}
        disabled={isDownloading || selectedScenariosForView.length === 0}
        className="simulation-button simulation-button-secondary" title="Export displayed scenarios to Excel">
        <Download size={15} aria-hidden="true" />{isDownloading ? 'Preparing…' : 'Export Excel'}
      </button>}
    </div>
    <ExploreControls dimensions={axes.map((axis, i) => ({ name: axis.name, size: shape[i] }))}
      fixed={fixedDims} values={dimValues} labels={shape.map((size, axis) => getLabels(size, axis))}
      onView={exploration.onView} onValue={exploration.onValue} />
    {scenarios.length > 1 && <details className="displayed-scenarios">
      <summary>{selectedScenarioIds.length} of {scenarios.length} scenarios displayed</summary>
      <div className="displayed-scenario-options">
        {scenarios.map(scenario => <label key={scenario.id}>
          <input type="checkbox" checked={selectedScenarioIds.includes(scenario.id)} onChange={e => {
            setSelectedScenarioIds(e.target.checked ? [...selectedScenarioIds, scenario.id] : selectedScenarioIds.filter(id => id !== scenario.id));
          }} />{scenario.name}
        </label>)}
        <button onClick={() => setSelectedScenarioIds(scenarios.map(s => s.id))} className="simulation-text-button">All</button>
        <button onClick={() => setSelectedScenarioIds([])} className="simulation-text-button">None</button>
      </div>
    </details>}
    <div className="results-chart">{renderComparison()}</div>
  </div>;
};

const styles: { [key: string]: React.CSSProperties } = {
  empty: {
    textAlign: 'center',
    padding: '60px 20px',
    color: 'var(--color-text-subtle)',
  },
  scalarContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '16px',
  },
  scalarCard: {
    padding: '20px',
    backgroundColor: 'var(--color-bg)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border)',
  },
  scalarHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  scalarValue: {
    fontSize: '28px',
    fontWeight: 700,
    color: 'var(--color-primary)',
    marginBottom: '8px',
  },
  infoBox: {
    padding: '40px',
    textAlign: 'center',
    backgroundColor: '#f0f9ff',
    border: '1px dashed #0ea5e9',
    borderRadius: 'var(--radius-md)',
    color: 'var(--color-text)',
  },
  dimensionInfo: {
    padding: '0 0 12px',
    backgroundColor: 'transparent',
    borderRadius: 'var(--radius-md)',
    borderBottom: '1px solid var(--color-border)',
    marginBottom: '16px',
  },
  dimensionExplanation: {
    fontSize: '14px',
    color: 'var(--color-text-subtle)',
    marginTop: '12px',
  },
  scenarioLegend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    marginTop: '12px',
    padding: '8px 12px',
    backgroundColor: 'white',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border)',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '14px',
    fontWeight: 500,
  },
  legendColor: {
    width: '16px',
    height: '16px',
    borderRadius: '3px',
    border: '1px solid rgba(0,0,0,0.1)',
  },
};

export default ScenarioVisualization;


